import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useEnterExitAnimation } from '../../util/useEnterExitAnimation.js';
import { useInteraction } from '../../util/useInteraction.js';
import { useLockBodyScroll } from '../../util/useLockBodyScroll.js';
import { useOverlay } from '../../util/useOverlay.js';
import useScreenSize from '../../util/useScreenSize.js';
import useUniqueId from '../../util/useUniqueId.js';
import { SIDE_SHEET_TRANSITION, ANIMATION_FALL_BACK_TIME_MS, SIDE_SHEET_CLOSE_BUTTON_ARIA_LABEL, SIDE_SHEET_TRANSFORM, SIDE_SHEET_CLOSE_KEY } from './constants.js';
import { getFocusTrapProps, mapContainerBoxTransform, mapContainerBoxWidth, mapContainerBoxBorderRadius, mapContainerBoxPosition, getContainerBoxStaticProps, getContainerBoxAriaProps } from './SideSheet.mappers.js';
import { useDirectionContext } from '../../contexts/session/Locale.js';

var _excluded = ["visible", "placement", "size", "zIndex", "header", "aria-label", "onClose"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var useSideSheet = function useSideSheet(_ref) {
  var _header$props;
  var visible = _ref.visible,
    placement = _ref.placement,
    size = _ref.size,
    zIndex = _ref.zIndex,
    header = _ref.header,
    ariaLabel = _ref['aria-label'],
    onClose = _ref.onClose,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useOverlay = useOverlay({
      visible: visible,
      transition: SIDE_SHEET_TRANSITION,
      animationFallBackTimeMs: ANIMATION_FALL_BACK_TIME_MS,
      zIndex: zIndex,
      height: '100dvh',
      onClick: onClose
    }),
    overlayProps = _useOverlay.overlayProps,
    shouldRenderOverlay = _useOverlay.shouldRenderOverlay;
  var focusTrapProps = getFocusTrapProps(visible);
  var _ref2 = (_header$props = header === null || header === void 0 ? void 0 : header.props) !== null && _header$props !== void 0 ? _header$props : {},
    title = _ref2.title,
    titleId = _ref2.titleId,
    subtitle = _ref2.subtitle,
    subtitleId = _ref2.subtitleId,
    closeButton = _ref2.closeButton;
  var internalTitleId = useUniqueId();
  var internalSubtitleId = useUniqueId();
  var labelledBy = titleId !== null && titleId !== void 0 ? titleId : internalTitleId;
  var describedBy = subtitleId !== null && subtitleId !== void 0 ? subtitleId : internalSubtitleId;
  var onCloseButtonClick = React.useCallback(function (e) {
    var _closeButton$onClick;
    onClose();
    closeButton === null || closeButton === void 0 || (_closeButton$onClick = closeButton.onClick) === null || _closeButton$onClick === void 0 || _closeButton$onClick.call(closeButton, e);
  }, [onClose, closeButton]);
  var getHeader = function getHeader() {
    var _closeButton$ariaLab;
    if (!header) return null;
    var headerProps = _objectSpread(_objectSpread({
      titleId: labelledBy,
      subtitleId: describedBy
    }, header === null || header === void 0 ? void 0 : header.props), {}, {
      closeButton: _objectSpread(_objectSpread({}, closeButton), {}, {
        onClick: onCloseButtonClick,
        'aria-label': (_closeButton$ariaLab = closeButton === null || closeButton === void 0 ? void 0 : closeButton['aria-label']) !== null && _closeButton$ariaLab !== void 0 ? _closeButton$ariaLab : SIDE_SHEET_CLOSE_BUTTON_ARIA_LABEL
      })
    });
    return /*#__PURE__*/React.cloneElement(header, headerProps);
  };
  var direction = useDirectionContext();
  var lockBodyScroll = useLockBodyScroll();
  var screenSize = useScreenSize();
  var _useEnterExitAnimatio = useEnterExitAnimation({
      initialProps: {
        transform: mapContainerBoxTransform(direction, placement)
      },
      enterProps: {
        transform: SIDE_SHEET_TRANSFORM
      },
      animationOn: visible,
      transition: SIDE_SHEET_TRANSITION,
      animationFallBackTimeMs: ANIMATION_FALL_BACK_TIME_MS
    }),
    transitionProps = _useEnterExitAnimatio.transitionProps,
    shouldRenderBottomSheet = _useEnterExitAnimatio.shouldRender;
  var onKeyDown = React.useCallback(function (event) {
    return event.key === SIDE_SHEET_CLOSE_KEY && onClose();
  }, [onClose]);
  var onClick = React.useCallback(function (event) {
    return event.stopPropagation();
  }, []);
  var _useInteraction = useInteraction({
      onClick: onClick,
      onKeyDown: onKeyDown
    }),
    interactionProps = _useInteraction.interactionProps;
  var containerBoxProps = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    width: mapContainerBoxWidth(screenSize, size),
    borderRadius: mapContainerBoxBorderRadius(direction, placement)
  }, mapContainerBoxPosition(direction, placement)), getContainerBoxStaticProps()), getContainerBoxAriaProps(title, subtitle, ariaLabel, labelledBy, describedBy)), transitionProps), interactionProps), {}, {
    onClick: onClick
  });
  return {
    visible: visible,
    zIndex: zIndex,
    onClose: onClose,
    overlayProps: overlayProps,
    focusTrapProps: focusTrapProps,
    header: getHeader(),
    containerBoxProps: _objectSpread(_objectSpread({
      ref: lockBodyScroll
    }, containerBoxProps), rest),
    shouldRenderOverlay: shouldRenderOverlay,
    shouldRenderBottomSheet: shouldRenderBottomSheet
  };
};

export { useSideSheet };
//# sourceMappingURL=useSideSheet.js.map
